/** PDF export: capture element with html-to-image, wrap with jsPDF. */

export interface PdfExportOptions {
  filename?: string;
  /** A4 = "a4" (default), letter, etc. */
  format?: string | [number, number];
  orientation?: 'portrait' | 'landscape';
  /** Pre-clone callback to remove edit-only chrome from the snapshot. */
  filter?: (node: Element) => boolean;
}

export async function exportElementToPdf(
  element: HTMLElement,
  options: PdfExportOptions = {},
): Promise<void> {
  await document.fonts.ready;

  const [{ toPng }, jspdfMod] = await Promise.all([import('html-to-image'), import('jspdf')]);
  const Pdf = jspdfMod.jsPDF;

  const dataUrl = await toPng(element, {
    pixelRatio: 2,
    backgroundColor: '#ffffff',
    cacheBust: true,
    filter:
      options.filter ??
      ((node) => {
        if (!(node instanceof Element)) return true;
        if (node.matches('[data-testid="editor-root"] > div:first-child')) return false;
        if (node.matches('[data-testid="history-panel"]')) return false;
        if (node.matches('[data-testid="ai-drawer"]')) return false;
        if (node.matches('[data-testid="undo-toast"]')) return false;
        if (node.matches('[data-testid="template-picker"]')) return false;
        if (node.classList.contains('react-flow__controls')) return false;
        if (node.classList.contains('react-flow__attribution')) return false;
        return true;
      }),
  });

  // Compute the image size, then size the PDF page to match its aspect.
  const img = await loadImage(dataUrl);

  const orientation = options.orientation ?? (img.width > img.height ? 'landscape' : 'portrait');
  const format = options.format ?? 'a4';

  const pdf = new Pdf({ orientation, unit: 'pt', format, compress: true });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const scale = Math.min(pageWidth / img.width, pageHeight / img.height);
  const w = img.width * scale;
  const h = img.height * scale;
  const offsetX = (pageWidth - w) / 2;
  const offsetY = 16;

  // If the image is way taller than one page, paginate vertically.
  if (h <= pageHeight - offsetY - 16) {
    pdf.addImage(dataUrl, 'PNG', offsetX, offsetY, w, h);
  } else {
    paginateVertically(pdf, dataUrl, img, pageWidth, pageHeight);
  }

  pdf.save(options.filename ?? 'fiche.pdf');
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function paginateVertically(
  pdf: import('jspdf').jsPDF,
  dataUrl: string,
  img: HTMLImageElement,
  pageWidth: number,
  pageHeight: number,
): void {
  // We render the same image on each page, but offset the y coordinate so
  // each page shows a different slice. The image's full height is mapped
  // onto pageWidth / img.width * img.height pt; we then walk through that
  // total height in chunks of pageHeight.
  const scale = pageWidth / img.width;
  const totalHeightPt = img.height * scale;
  let y = 0;
  while (y < totalHeightPt) {
    pdf.addImage(dataUrl, 'PNG', 0, -y, pageWidth, totalHeightPt);
    y += pageHeight;
    if (y < totalHeightPt) pdf.addPage();
  }
}
